import React, { useState } from 'react';
import { useClientes, useCriarCliente, useAtualizarCliente, useRemoverCliente } from '../../hooks/useClientes';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useAuth } from '../../contexts/AuthContext';
import { Cliente } from '../../types';
import Button from '../../components/Common/Button';
import ConfirmModal from '../../components/Common/ConfirmModal';
import api from '../../api/client';
import toast from 'react-hot-toast';

interface ClienteFormData {
  nome: string;
  telefone: string;
  email: string;
  endereco: string;
}

const Clientes: React.FC = () => {
  usePageTitle('Clientes');
  const { user } = useAuth();
  const { data: clientes, isLoading, refetch } = useClientes();
  const { mutate: criarCliente, isPending: criando } = useCriarCliente();
  const { mutate: atualizarCliente, isPending: atualizando } = useAtualizarCliente();
  const { mutate: removerCliente } = useRemoverCliente();

  const [mostrarModal, setMostrarModal] = useState(false);
  const [mostrarModalNotas, setMostrarModalNotas] = useState(false);
  const [clienteEditando, setClienteEditando] = useState<Cliente | null>(null);
  const [clienteNotas, setClienteNotas] = useState<Cliente | null>(null);
  const [novaNota, setNovaNota] = useState('');
  const [formData, setFormData] = useState<ClienteFormData>({
    nome: '',
    telefone: '',
    email: '',
    endereco: '',
  });

  const [confirmarRemocao, setConfirmarRemocao] = useState<{
    isOpen: boolean;
    clienteId: number | null;
    clienteNome: string;
  }>({
    isOpen: false,
    clienteId: null,
    clienteNome: '',
  });

  // Função para aplicar máscara de telefone
  const aplicarMascaraTelefone = (valor: string) => {
    const numeros = valor.replace(/\D/g, '');
    if (numeros.length <= 10) {
      return numeros.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    } else {
      return numeros.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
    }
  };

  // Função para formatar telefone na exibição
  const formatarTelefone = (telefone: string) => {
    const numeros = telefone.replace(/\D/g, '');
    if (numeros.length === 11) {
      return numeros.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (numeros.length === 10) {
      return numeros.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return telefone;
  };

  const abrirModalNovo = () => {
    setClienteEditando(null);
    setFormData({
      nome: '',
      telefone: '',
      email: '',
      endereco: '',
    });
    setMostrarModal(true);
  };

  const abrirModalEditar = (cliente: Cliente) => {
    setClienteEditando(cliente);
    setFormData({
      nome: cliente.nome,
      telefone: cliente.telefone,
      email: cliente.email || '',
      endereco: cliente.endereco || '',
    });
    setMostrarModal(true);
  };

  const abrirModalNotas = async (cliente: Cliente) => {
    try {
      const response = await api.get(`/clientes/${cliente.id}`);
      setClienteNotas(response.data);
      setNovaNota('');
      setMostrarModalNotas(true);
    } catch (error: any) {
      toast.error('Erro ao carregar notas do cliente');
    }
  };

  const fecharModal = () => {
    setMostrarModal(false);
    setClienteEditando(null);
    setFormData({
      nome: '',
      telefone: '',
      email: '',
      endereco: '',
    });
  };

  const fecharModalNotas = () => {
    setMostrarModalNotas(false);
    setClienteNotas(null);
    setNovaNota('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.estabelecimentoId) {
      toast.error('Usuário sem estabelecimento vinculado');
      return;
    }

    const clienteData = {
      nome: formData.nome,
      telefone: formData.telefone.replace(/\D/g, ''),
      email: formData.email || undefined,
      endereco: formData.endereco || undefined,
    };

    if (clienteEditando) {
      atualizarCliente(
        {
          id: clienteEditando.id,
          ...clienteData,
        },
        {
          onSuccess: () => fecharModal(),
        }
      );
    } else {
      criarCliente(
        {
          ...clienteData,
          estabelecimentoId: user.estabelecimentoId,
        },
        {
          onSuccess: () => fecharModal(),
        }
      );
    }
  };

  const handleAdicionarNota = async () => {
    if (!clienteNotas || !novaNota.trim()) return;

    try {
      await api.post(`/clientes/${clienteNotas.id}/notas`, {
        nota: novaNota.trim(),
      });
      toast.success('Nota adicionada com sucesso!');

      // Recarrega os dados do cliente
      const response = await api.get(`/clientes/${clienteNotas.id}`);
      setClienteNotas(response.data);
      setNovaNota('');
      refetch();
    } catch (error: any) {
      const message = error.response?.data?.error || 'Erro ao adicionar nota';
      toast.error(message);
    }
  };

  const abrirConfirmacaoRemocao = (id: number, nome: string) => {
    setConfirmarRemocao({
      isOpen: true,
      clienteId: id,
      clienteNome: nome,
    });
  };

  const fecharConfirmacaoRemocao = () => {
    setConfirmarRemocao({
      isOpen: false,
      clienteId: null,
      clienteNome: '',
    });
  };

  const handleConfirmarRemocao = () => {
    if (confirmarRemocao.clienteId) {
      removerCliente(confirmarRemocao.clienteId, {
        onSuccess: () => {
          fecharConfirmacaoRemocao();
        },
      });
    }
  };

  const handleTelefoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valorFormatado = aplicarMascaraTelefone(e.target.value);
    setFormData({ ...formData, telefone: valorFormatado });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';

    try {
      // Remove o 'Z' e adiciona o offset de timezone para evitar problemas de conversão
      const date = new Date(dateString);

      // Verifica se a data é válida
      if (isNaN(date.getTime())) {
        return '-';
      }

      // Formata a data manualmente para garantir o formato correto
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch {
      return '-';
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Clientes</h1>
        <Button onClick={abrirModalNovo} variant="primary" size="md">
          Novo Cliente
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-8">
          <p className="text-gray-600">Carregando clientes...</p>
        </div>
      ) : clientes && clientes.length > 0 ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Telefone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Endereço
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Cadastro
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {clientes.map((cliente) => (
                <tr key={cliente.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{cliente.nome}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatarTelefone(cliente.telefone)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{cliente.email || '-'}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{cliente.endereco || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDate(cliente.dataCriacao)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => abrirModalNotas(cliente)}
                      className="text-green-600 hover:text-green-900 mr-4"
                      title="Gerenciar notas"
                    >
                      Notas
                    </button>
                    <button
                      onClick={() => abrirModalEditar(cliente)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => abrirConfirmacaoRemocao(cliente.id, cliente.nome)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Remover
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600">Nenhum cliente cadastrado.</p>
        </div>
      )}

      {/* Modal de Confirmação de Remoção */}
      <ConfirmModal
        isOpen={confirmarRemocao.isOpen}
        title="Confirmar Remoção"
        message={`Tem certeza que deseja remover o cliente "${confirmarRemocao.clienteNome}"? Esta ação não pode ser desfeita.`}
        confirmText="Sim, remover"
        cancelText="Cancelar"
        onConfirm={handleConfirmarRemocao}
        onCancel={fecharConfirmacaoRemocao}
      />

      {/* Modal de Criar/Editar Cliente */}
      {mostrarModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {clienteEditando ? 'Editar Cliente' : 'Novo Cliente'}
              </h3>
              <button
                onClick={fecharModal}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome *
                  </label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone * (com DDD)
                  </label>
                  <input
                    type="tel"
                    value={formData.telefone}
                    onChange={handleTelefoneChange}
                    placeholder="(00) 00000-0000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    maxLength={15}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="exemplo@email.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Endereço
                  </label>
                  <input
                    type="text"
                    value={formData.endereco}
                    onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                    placeholder="Rua, número, bairro, cidade"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <Button
                  type="button"
                  onClick={fecharModal}
                  variant="secondary"
                  size="md"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  disabled={criando || atualizando}
                >
                  {criando || atualizando ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Notas */}
      {mostrarModalNotas && clienteNotas && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Notas de {clienteNotas.nome}
              </h3>
              <button
                onClick={fecharModalNotas}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            <div className="space-y-4">
              {/* Lista de Notas */}
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md p-4">
                {clienteNotas.notas && clienteNotas.notas.length > 0 ? (
                  <ul className="space-y-2">
                    {clienteNotas.notas.map((nota, index) => (
                      <li key={index} className="p-3 bg-gray-50 rounded border-l-4 border-blue-500">
                        <p className="text-sm text-gray-700">{nota}</p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 text-center py-4">Nenhuma nota cadastrada.</p>
                )}
              </div>

              {/* Adicionar Nova Nota */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adicionar Nova Nota
                </label>
                <textarea
                  value={novaNota}
                  onChange={(e) => setNovaNota(e.target.value)}
                  placeholder="Digite uma nova nota..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  onClick={fecharModalNotas}
                  variant="secondary"
                  size="md"
                >
                  Fechar
                </Button>
                <Button
                  type="button"
                  onClick={handleAdicionarNota}
                  variant="primary"
                  size="md"
                  disabled={!novaNota.trim()}
                >
                  Adicionar Nota
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clientes;
